import type { SlideData } from './contracts'
import type { QualAnalysisSlideData, RatingLikertSlideData } from './renderer-internal-types'
import { readDivergingLikertSlide } from '../plots/diverging-likert/adapters'

export function hasRetiredQualLayout(slide: Pick<QualAnalysisSlideData, 'layoutDiagnostics' | 'displayOptions'>): boolean {
  return (slide.layoutDiagnostics?.resolvedLayout || slide.layoutDiagnostics?.requestedLayout
    || slide.displayOptions?.layout) !== 'editorial'
}

/**
 * A rating slide whose plot says `status: 'unsupported'` (plots plan P): Python's
 * stamp on a slide built with `plot`, else the legacy reader's status for a
 * stored slide (today's resolver over the same bounded rows the renderer draws).
 */
export function hasRetiredRatingScale(slide: RatingLikertSlideData): boolean {
  if (!slide.scale) return false // Invalid payloads belong to the schema validator.
  return readDivergingLikertSlide(slide).payload.data.status === 'unsupported'
}

/**
 * Renderer types that are gone from the registry. A stored deck may still
 * carry them, so they are hidden here rather than drawn as an unknown slide.
 * The Python twin is `contracts.RETIRED_SLIDE_TYPES`, which drops the plan
 * item. `cut-exhibit` (Step 4b) is drawn by the split choice bars instead
 * (cuts throughout, O-5). `segment-comparison` was removed by the owner
 * (2026-09-29): a split's other cuts stay in the prose. `kpi-strip` is gone
 * from this branch's registry; decks stored on dev carry it (lead D22).
 */
const RETIRED_SLIDE_TYPES: ReadonlySet<string> = new Set(['cut-exhibit', 'segment-comparison', 'kpi-strip'])

export function slideRetirementReason(slide: SlideData): 'slideType' | 'qualLayout' | 'ratingScale' | null {
  if (RETIRED_SLIDE_TYPES.has(slide.type)) return 'slideType'
  if (slide.type === 'qual-analysis' && hasRetiredQualLayout(slide)) return 'qualLayout'
  if (slide.type === 'rating-likert' && hasRetiredRatingScale(slide as RatingLikertSlideData)) return 'ratingScale'
  return null
}

/** Original positions are retained for edits and persisted hiddenSlideIndices. */
export function renderableSlideIndices(slides: SlideData[]): number[] {
  return slides.flatMap((slide, index) => slideRetirementReason(slide) ? [] : [index])
}
